import tippy, { followCursor } from 'tippy.js';
import LocaleService from 'services/loader-handler-services/locale-service';
import $ from 'jquery';
import Chaplin from 'chaplin';

/**
 * Helper functions
 */
function isChromeOS() {
  return /CrOS/i.test(navigator.userAgent);
}

/**
 * TippyJS Plugins
 */

const hideOnCloseKeys = {
  name: 'hideOnCloseKeys',
  defaultValue: true,
  fn(instance) {
    let previousFocusedElem = null;
    function onEscKey(event) {
      if (event.keyCode === 27) {
        if (previousFocusedElem !== instance.reference) {
          previousFocusedElem.focus();
        }
        instance.hide();
      }
    }
    function onTippyReferenceBlur() {
      // Skip on ChromeOS - we don't use aria-controls or aria-describedby
      if (isChromeOS()) {
        return;
      }
      $('[aria-controls="popover"]').each((_, el) => {
        if (el.id === instance.reference.id) {
          return;
        }
        $(el).removeAttr('aria-describedby');
      });
    }

    function isEventInsideTippy(event) {
      const target = event.target;
      return target === instance.reference || instance.reference.contains(target) || instance.popper.contains(target);
    }

    function onBlurKey(event) {
      // Scope to this tippy's own reference/popper so Tab/Enter/Space elsewhere on the page isn't swallowed
      if (!isEventInsideTippy(event)) {
        return;
      }
      if (event.keyCode === 13 || event.keyCode === 32 || event.keyCode === 9) {
        // only on escape does tippy handle automatically
        if (!isChromeOS()) {
          instance.reference.removeAttribute('aria-describedby');
        }
        instance.hide();
        // Prevent the key event from bubbling and retriggering the popover
        event.preventDefault();
        event.stopPropagation();
      }
    }
    return {
      onCreate() {
        // When a tippy link is created, listen for blur on the link itself, too
        if (!isChromeOS()) {
          instance.reference.addEventListener('blur', onTippyReferenceBlur);
        }
      },
      onShow() {
        previousFocusedElem = document.activeElement;
        document.addEventListener('keydown', onEscKey);
      },
      onAfterUpdate() {
        document.addEventListener('keydown', onBlurKey);
      },
      onHide() {
        document.removeEventListener('keydown', onBlurKey);
        document.removeEventListener('keydown', onEscKey);
      },
    };
  },
};

const hideOnBlur = {
  name: 'hideOnBlur',
  fn(instance) {
    function onBlur(event) {
      const focusToReference = event.relatedTarget === instance.reference;
      const isFocusInside = instance.popper.contains(event.relatedTarget);
      if (!(isFocusInside || focusToReference)) {
        instance.hide();
      }
    }
    return {
      onShow() {
        instance.popper.addEventListener('blur', onBlur, true);
        instance.reference.addEventListener('blur', onBlur, true);
        window.addEventListener('touchmove', onBlur, instance);
      },
      onHide() {
        instance.popper.removeEventListener('blur', onBlur, true);
        instance.reference.removeEventListener('blur', onBlur, true);
        window.removeEventListener('touchmove', onBlur, instance);
      },
    };
  },
};

const checkSoundOrTippy = (instance, eventTarget, action = 'audio', event = {}) => {
  if (!eventTarget) {
    return false;
  }
  const audioControls = $(event.target).parents('.audio__controls').length;
  return document.querySelector(eventTarget) === instance.reference && action === 'tippy'
    ? audioControls
    : !audioControls;
};

const ignoreList = (event = {}) => {
  const ignoreList = ['navbar-audio-button'];
  return ignoreList.includes($(event.target).attr('id'));
};

const stopAudio = () => {
  //As there can only be one audio playing at a time. so it seems safe to use general audio:stop event
  Chaplin.mediator.publish(`audio:stop`, {
    disableTemporaryAutoplay: false,
  });
};

const checkSoundAndTippy = (instance, eventTarget, event) => {
  /**
   * We do not want to explicitly stop the audio with 'audio:stop' when the target is the audioPlay button.
   * The audio will be taken care by audioPlay back's handler itself.
   */
  if (checkSoundOrTippy(instance, eventTarget, 'audio', event) && !ignoreList(event)) {
    stopAudio();
  }
  instance.setProps({ preventClose: checkSoundOrTippy(instance, eventTarget, 'tippy', event) });
};

/**
 * TippyJS configurations
 */

function getPopoverContainer() {
  return $('body').find('#popover');
}

function announcePopoverContent(instance) {
  const content = (instance?.popper?.textContent || '').replace(/\s+/g, ' ').trim();

  if (!content) {
    return;
  }

  if (isChromeOS()) {
    // ChromeOS/ChromeVox: Use a separate announcer with no ARIA relationship to the button
    let announcer = document.getElementById('chromeos-popover-announcer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'chromeos-popover-announcer';
      announcer.className = 'visually-hidden';
      // No role - just use aria-live to avoid "alert" being announced
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      document.getElementById('site-container').appendChild(announcer);
    }

    announcer.textContent = content;
  }
  // Elsewhere the button's aria-describedby already reads the content when it expands, so an announcer would read it twice
}

const tooltipConfig = {
  allowHTML: false,
  interactive: false,
  trigger: 'mouseenter focus',
  theme: 'custom-tooltip',
  hideOnClick: true,
};

const popoverConfig = {
  allowHTML: true,
  interactive: true,
  trigger: 'click',
  theme: 'custom-light-border',
};

const defaultConfig = Object.freeze({
  allowHTML: true,
  aria: {
    expanded: false,
  },
  animation: 'scale',
  arrow: true,
  delay: 0,
  duration: [275, 250],
  hideOnClick: true,
  ignoreAttributes: false,
  inertia: false,
  interactive: false,
  interactiveBorder: 4,
  interactiveDebounce: 50,
  maxWidth: 320,
  placement: 'auto',
  plugins: [followCursor, hideOnCloseKeys, hideOnBlur],

  /**
   * Configuration event handlers
   */
  onClickOutside(instance, event) {
    checkSoundAndTippy(instance, this.audioData?.eventTarget, event);
  },

  onUntrigger(instance, event) {
    if (instance.props.allowHTML && !isChromeOS()) {
      instance.reference.setAttribute('aria-expanded', 'false');
    }
    getPopoverContainer().attr('aria-hidden', 'true');
    event.stopImmediatePropagation();
    checkSoundAndTippy(instance, this.audioData?.eventTarget, event);
  },

  onHide(instance) {
    if (instance.props.preventClose) {
      return false;
    }
  },
});

/**
 * Tippy Loader Service
 */

const TippyService = function () {
  this.current = null;
  this.add = function (element, config, allowHTML = false) {
    const settings = allowHTML ? popoverConfig : tooltipConfig;
    if (!allowHTML) {
      this.getLocaleString(config);
    } else {
      config.allowHTML = true;
    }

    settings.onCreate = instance => {
      if (isChromeOS() && allowHTML) {
        // Store the original aria-describedby to preserve button's accessible name
        if (instance.reference.hasAttribute('aria-describedby')) {
          instance.reference._originalAriaDescribedby = instance.reference.getAttribute('aria-describedby');
        }
        instance.reference.setAttribute('aria-expanded', 'false');
      }

      if (allowHTML) {
        // Don't remove role="button" - it's redundant but harmless and removing it might confuse screen readers
        if (!isChromeOS()) {
          instance.reference.setAttribute('aria-expanded', 'false');
          instance.reference.setAttribute('aria-describedby', 'popover-content');
          instance.reference.setAttribute('aria-controls', 'popover');
        }
        if (instance.reference.hasAttribute('data-additional-sr-text')) {
          instance.reference.setAttribute('aria-label', instance.reference.getAttribute('data-additional-sr-text'));
        }
      }
    };

    settings.onMount = instance => {
      if (isChromeOS() && allowHTML) {
        // Hide the popper from screen readers - content announced via separate live region
        instance.popper.setAttribute('aria-hidden', 'true');
      }
    };

    settings.onTrigger = (instance, event) => {
      if (instance.props.allowHTML) {
        // give screen readers a designated place to read popover content, that isnt cleared via onHide event
        if (!isChromeOS()) {
          instance.reference.setAttribute('aria-expanded', 'true');
          instance.reference.setAttribute('aria-describedby', 'popover-content');
        }
      }
      if (!isChromeOS()) {
        instance.reference.removeAttribute('aria-label');
      }
      // remove aria-live on tippy creation - progressive-disclosures are dynamic and causes issues with NVDA
      // https://github.com/nvaccess/nvda/issues/7996
      // https://github.com/nvaccess/nvda/issues/10512
      $('body')
        .find('.progressive-disclosure__container,.progressive-disclosure__hidden')
        .each((_, $e) => {
          $($e).parent().removeAttr('aria-live');
          $($e).removeAttr('aria-live');
        });
      if (!isChromeOS()) {
        instance.popper.ariaLive = 'polite';
      }

      instance.popper.setAttribute('data-popover-tippy', instance.props.placement);

      Chaplin.mediator.subscribe(`audio:playing`, data => {
        this.audioData = data || {};
      });
      this.current = instance.reference.id;
    };

    settings.onShown = instance => {
      if (instance.props.allowHTML) {
        if (isChromeOS()) {
          instance.popper.setAttribute('aria-hidden', 'true');
        }

        announcePopoverContent(instance);

        // ChromeOS: Set aria-expanded after announcement to avoid ChromeVox queueing delay
        if (isChromeOS()) {
          instance.reference.setAttribute('aria-expanded', 'true');
        }
      }
    };

    settings.onHidden = instance => {
      if (allowHTML) {
        const clickedAriaLabel = LocaleService.t(instance.reference.getAttribute('data-clicked-aria-label'));
        const srSpan = instance.reference.querySelector('.screen-reader-content');

        // Update the SR span regardless of platform - it's just DOM text
        if (clickedAriaLabel && srSpan) {
          srSpan.textContent = clickedAriaLabel;
        }

        if (!isChromeOS()) {
          if (clickedAriaLabel && !srSpan) {
            // No dedicated SR span - fall back to aria-label
            instance.reference.setAttribute('aria-label', clickedAriaLabel);
          } else if (!clickedAriaLabel && instance.reference.hasAttribute('data-additional-sr-text')) {
            instance.reference.setAttribute(
              'aria-label',
              `${instance.reference.getAttribute('data-additional-sr-text')} ${LocaleService.t('completed')}`
            );
          }
          instance.reference.setAttribute('aria-describedby', 'popover-content');
          instance.reference.setAttribute('aria-expanded', 'false');
        } else {
          // ChromeOS: Restore original aria-describedby to maintain button's accessible name
          if (instance.reference._originalAriaDescribedby) {
            instance.reference.setAttribute('aria-describedby', instance.reference._originalAriaDescribedby);
          } else {
            instance.reference.removeAttribute('aria-describedby');
          }
          instance.reference.setAttribute('aria-expanded', 'false');
          instance.popper.setAttribute('aria-hidden', 'true');
        }
        // add aria-live back to the next progressive disclosures when tippy view is closed and all the content is complete
        $('body').on('click', '.progressive-disclosure__next-button', function () {
          $('.progressive-disclosure__container,.progressive-disclosure__hidden').attr('aria-live', 'polite');
        });
      }
    };

    this.getReferenceElement(config);

    const finalConfig = Object.assign({}, defaultConfig, settings, config);

    if (isChromeOS() && allowHTML) {
      // Disable tippy's ARIA management - we handle it manually to avoid ChromeVox double-focus
      finalConfig.aria = {
        content: null,
        expanded: null,
      };
    }

    tippy(element, finalConfig);
  };

  this.getLocaleString = function (config) {
    if (config.content) {
      config.content = LocaleService.t(config.content);
    }
  };

  this.getReferenceElement = function (config) {
    const reference = config.appendTo || '#popover #popover-content';
    if (reference && reference !== 'parent') {
      config.appendTo = document.querySelector(reference) || document.body;
    }
  };

  this.updateContent = function (element, content) {
    if (element._tippy) {
      element._tippy.setContent(LocaleService.t(content));
    }
  };
};

export default new TippyService();
